import { useState, useMemo } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getLocationTypes } from "wasp/client/operations";
import { Filter, Plus, Upload } from "lucide-react";
import LoadingSpinner from "./layout/LoadingSpinner";
import { EnhancedActionButton } from "../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../components/ui/table-filter-layout";
import { BulkUploadDialog } from "./bulk-upload/components/BulkUploadDialog";
import { locationTypeConfig } from "./bulk-upload/configs";
import { useBulkUploadEnabled } from "./hooks/useBulkUploadEnabled";

export default function LocationTypesPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return <div>Loading...</div>;
  }

  const { data: locationTypes = [], isLoading, error, refetch } = useQuery(getLocationTypes);
  const { isEnabled: isBulkUploadEnabled } = useBulkUploadEnabled();

  const [searchTerm, setSearchTerm] = useState("");
  const [labelFilter, setLabelFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const filteredData = useMemo(() => {
    return (locationTypes as any[]).filter((type: any) => {
      if (searchTerm && !type.key.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (labelFilter && !type.label.toLowerCase().includes(labelFilter.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [locationTypes, searchTerm, labelFilter]);

  const hasData = (locationTypes as any[]).length > 0;

  return (
    <>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Location Categories</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Define and manage location types for the platform ({filteredData.length} total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filter
                </button>
                {isBulkUploadEnabled && (
                  <EnhancedActionButton
                    onClick={() => setShowBulkUpload(true)}
                    icon={Upload}
                    label="Bulk Upload"
                    variant="secondary"
                  />
                )}
                <EnhancedActionButton
                  onClick={() => navigate('/admin/taxonomy/location-categories/new')}
                  icon={Plus}
                  label="Create Type"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Spacer to push content below fixed header */}
        <div className="h-20" />

        {/* Table Header with Filters — flows in document, pushes content down */}
        {hasData && (
          <TableFilterLayout
            showFilters={showFilters}
            columns={[
              { title: 'Key', span: 2 },
              { title: 'Label', span: 4 },
              { title: 'Description', span: 6 }
            ]}
            filters={[
              {
                span: 2,
                content: (
                  <input
                      type="text"
                      placeholder="Search key..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                )
              },
              {
                span: 4,
                content: (
                  <input
                    type="text"
                    placeholder="Filter by label..."
                    value={labelFilter}
                    onChange={(e) => setLabelFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )
              },
              { span: 6, content: <div className="h-10" /> }
            ]}
          />
        )}

        {/* Content */}
        <div className="w-full">
          {isLoading && <LoadingSpinner />}
          {error && (
            <div className="p-4 text-center text-red-600">
              Error loading location types: {error.message}
            </div>
          )}

          {!isLoading && !error && filteredData.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p className="text-lg font-medium">No location types found</p>
              <p className="text-sm mt-2">
                {searchTerm || labelFilter ? "Try adjusting your filters" : "Click 'Create Type' to add your first location type"}
              </p>
            </div>
          )}

          {filteredData.length > 0 && (
            <>
              {filteredData.map((type: any) => (
                <div
                  key={type.id}
                  className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-sidebar-hover transition-colors grid grid-cols-12 gap-4 items-center border-b border-gray-200 dark:border-gray-700"
                >
                  <div className="col-span-2 overflow-hidden min-w-0">
                    <Link
                      to={`/admin/taxonomy/location-categories/${type.id}`}
                      className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600">
                        {type.key}
                      </span>
                    </Link>
                  </div>
                  <div className="col-span-4 overflow-hidden min-w-0">
                    <Link
                      to={`/admin/taxonomy/location-categories/${type.id}`}
                      className="text-sm text-gray-900 dark:text-white hover:text-blue-600 transition-colors"
                    >
                      {type.label}
                    </Link>
                  </div>
                  <div className="col-span-6 overflow-hidden min-w-0">
                    <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                      {type.description || '-'}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      <BulkUploadDialog
        open={showBulkUpload}
        onOpenChange={setShowBulkUpload}
        config={locationTypeConfig}
        onComplete={() => refetch()}
      />
    </>
  );
}



